<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="房间" prop="roomId">
        <el-select
          v-model="queryParams.roomId"
          class="!w-240px"
          clearable
          filterable
          placeholder="请选择房间"
          @change="handleQuery"
        >
          <el-option v-for="item in roomList" :key="item.id" :label="item.roomNo || ('#' + item.id)" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="变更类型" prop="changeType">
        <el-select
          :model-value="queryParams.changeType as any"
          @update:model-value="(val: any) => queryParams.changeType = val"
          class="!w-240px"
          clearable
          placeholder="请选择变更类型"
        >
          <el-option label="手动变更" value="手动变更" />
          <el-option label="自动变更" value="自动变更" />
          <el-option label="PMS同步" value="PMS同步" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column align="center" label="房间号" prop="roomNo" min-width="120">
        <template #default="scope">
          <el-tag v-if="scope.row.roomNo" type="info">{{ scope.row.roomNo }}</el-tag>
          <span v-else class="text-gray-400">#{{ scope.row.roomId }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="状态变更" min-width="200">
        <template #default="scope">
          <el-tag :type="getRoomStateTag(scope.row.oldState)">
            {{ getRoomStateLabel(scope.row.oldState) }}
          </el-tag>
          <Icon class="mx-5px" icon="ep:right" />
          <el-tag :type="getRoomStateTag(scope.row.newState)">
            {{ getRoomStateLabel(scope.row.newState) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="变更类型" prop="changeType">
        <template #default="scope">
          <el-tag>{{ getChangeTypeLabel(scope.row.changeType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作人" prop="operatorName">
        <template #default="scope">
          <el-tag v-if="scope.row.operatorName" type="success">{{ scope.row.operatorName }}</el-tag>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" prop="remark" min-width="150" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as roomStateApi from '@/api/hotel/roomState'
import * as roomApi from '@/api/hotel/room'
import { hotelLookup } from '@/utils/hotel/lookup'

defineOptions({ name: 'HotelRoomState' })

const message = useMessage()

const loading = ref(true)
const total = ref(0)
const list = ref([])

// 房间下拉列表
const roomList = ref<any[]>([])
const getRoomList = async () => {
  const data = await roomApi.listAllRooms()
  roomList.value = data.list || []
}

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  roomId: undefined,
  changeType: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await roomStateApi.getRoomStatePage(queryParams)
    // 补全房间号
    const roomLookup = await hotelLookup.fillRoomNos(data.list.map((r: any) => r.roomId))
    hotelLookup.patchList(data.list, 'roomId', 'roomNo', roomLookup)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

// 房态映射
const roomStateMap: Record<number, string> = {
  0: '空闲', 1: '预订', 2: '入住', 3: '退房', 4: '清洁', 5: '维修', 6: '锁定'
}
const roomStateTagMap: Record<number, string> = {
  0: 'success', 1: 'warning', 2: 'primary', 3: 'info', 4: '', 5: 'danger', 6: 'danger'
}
const getRoomStateLabel = (s: number) => roomStateMap[s] || `状态${s}`
const getRoomStateTag = (s: number) => roomStateTagMap[s] || 'info'

// 变更类型映射
const changeTypeMap: Record<string, string> = {
  'MANUAL': '手动', 'AUTO': '自动', 'ORDER': '订单触发', 'PMS_SYNC': 'PMS同步',
  'CLEANING': '清洁', 'MAINTENANCE': '维修', 'CHECKIN': '入住', 'CHECKOUT': '退房'
}
const getChangeTypeLabel = (t: string) => changeTypeMap[t] || t || '-'

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

onMounted(() => {
  getList()
  getRoomList()
})
</script>
